// Relations between issues, from one issue's side, as the server keeps them
// (board/relations.rs) and today's issue page names them.
import type { Relation, RelationKind } from '../api/types'

/** The kinds in the order the server lists them and the page groups them, with today's names. */
export const RELATION_KINDS: readonly { kind: RelationKind; name: string }[] = [
  { kind: 'blocks', name: 'Blocks' },
  { kind: 'blocked_by', name: 'Blocked by' },
  { kind: 'related', name: 'Related' },
  { kind: 'duplicate_of', name: 'Duplicate of' },
  { kind: 'duplicated_by', name: 'Duplicated by' },
]

/** The same relation from the other issue's side. */
export function inverseOf(kind: RelationKind): RelationKind {
  switch (kind) {
    case 'blocks': return 'blocked_by'
    case 'blocked_by': return 'blocks'
    case 'related': return 'related'
    case 'duplicate_of': return 'duplicated_by'
    case 'duplicated_by': return 'duplicate_of'
  }
}

const order = (kind: RelationKind) => RELATION_KINDS.findIndex((k) => k.kind === kind)

/** A key's project and number: `Poulpe-012` is `['Poulpe', 12]`. */
function keyParts(key: string): [string, number] {
  const at = key.lastIndexOf('-')
  return [key.slice(0, at), Number(key.slice(at + 1))]
}

/** Relations in the server's order: by kind, then by the other's key, its project's key compared as a string, then its number. */
export function sortRelations(relations: readonly Relation[]): Relation[] {
  return [...relations].sort((a, b) => {
    if (a.kind !== b.kind) return order(a.kind) - order(b.kind)
    const [pa, na] = keyParts(a.key)
    const [pb, nb] = keyParts(b.key)
    return pa < pb ? -1 : pa > pb ? 1 : na - nb
  })
}
